import { useQuery } from '@tanstack/react-query';
import {
  DISCOUNT_LEVEL_MODE_OPTIONS,
  DISCOUNT_LEVEL_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  USER_STATUS_OPTIONS,
} from '@/shared/constants/users';
import { toOptions } from '@/shared/lib/options';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { fetchPartnerGroups, fetchRoles } from './api';
import { PlaceFields } from './PlaceFields';
import { CheckField, SelectField, TextField, type ProfileFormApi } from './ProfileInputs';
import {
  hasAutoparkRole,
  isCustomerRole,
  isServiceRole,
  type ProfileMode,
  type ProfilePermissions,
} from './profileForm';
import { ServiceLocationFields } from './ServiceLocationFields';
import { ServiceProfileFields } from './ServiceProfileFields';

type Props = {
  form: ProfileFormApi;
  mode: ProfileMode;
  permissions: ProfilePermissions;
};

const REQUIRED = <span className="text-red">*</span>;
const BLOCKED_STATUS = '4';
const VERIFIED_STATUS = '5';
const YESTERDAY = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const RolesField = ({ form }: { form: ProfileFormApi }) => {
  const roles = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  return (
    <FormField label={<>Роль {REQUIRED}</>} errors={form.errors.roles}>
      <MultiSelect
        options={toOptions(roles.data)}
        value={form.values.roles}
        isLoading={roles.isLoading}
        width={400}
        onChange={(selected) => form.patch({ roles: selected }, 'roles')}
      />
    </FormField>
  );
};

const AutoparkFields = ({ form }: { form: ProfileFormApi }) => {
  const { values, errors, patch } = form;
  const groups = useQuery({ queryKey: ['partner-groups'], queryFn: fetchPartnerGroups });

  return (
    <>
      <SelectField
        form={form}
        field="partnerGroupId"
        errorKey="group_partner"
        label="Партнер, от которого пришел автопарк"
        options={toOptions(groups.data)}
        isLoading={groups.isLoading}
        clearable
      />
      <FormField label="Система оплат" errors={errors.payment_method}>
        {PAYMENT_METHOD_OPTIONS.map(({ value, label }) => (
          <label key={value} style={{ display: 'block', fontWeight: 'normal' }}>
            <input
              type="radio"
              name="paymentMethod"
              checked={values.paymentMethod === value}
              onChange={() => patch({ paymentMethod: value }, 'payment_method')}
            />{' '}
            {label}
          </label>
        ))}
      </FormField>
      <TextField form={form} field="contractNumber" errorKey="contract_number" label="Номер договора" type="number" />
      <FormField label="Дата заключения договора" errors={errors.contract_time}>
        <input
          type="date"
          className="form-control input-sm"
          style={{ maxWidth: 200 }}
          max={YESTERDAY}
          value={values.contractDate}
          onChange={(e) => patch({ contractDate: e.target.value }, 'contract_time')}
        />
      </FormField>
    </>
  );
};

const LoyaltyLevelFields = ({ form }: { form: ProfileFormApi }) => (
  <>
    <SelectField
      form={form}
      field="discountLevelMode"
      errorKey="discount_level_mode"
      label="Режим уровня лояльности"
      options={DISCOUNT_LEVEL_MODE_OPTIONS}
    />
    <SelectField
      form={form}
      field="discountLevel"
      errorKey="discount_level"
      label="Уровень лояльности"
      options={DISCOUNT_LEVEL_OPTIONS}
      disabled={form.values.discountLevelMode !== '1'}
    />
  </>
);

/** Balances, admin comments and the card-payments correction (edit page only). */
const BalanceCommentFields = ({ form }: { form: ProfileFormApi }) => {
  const { values } = form;
  return (
    <>
      <TextField
        form={form}
        field="comment"
        errorKey="comment"
        label={<>Комментарий администратора {values.balance && REQUIRED}</>}
        placeholder="При изменении баланса комментарий обязателен"
        disabled={!values.balance}
      />
      <TextField
        form={form}
        field="publicBalanceComment"
        errorKey="public_balance_comment"
        label="Публичный комментарий"
        placeholder="При изменении баланса"
      />
      <TextField form={form} field="bonusBalance" errorKey="bonus_balance" label="Бонусный баланс" />
      <TextField
        form={form}
        field="bonusComment"
        errorKey="bonus_comment"
        label={<>Комментарий администратора (бонусы) {values.bonusBalance && REQUIRED}</>}
        placeholder="При изменении бонусного баланса комментарий обязателен"
        disabled={!values.bonusBalance}
      />
      <TextField
        form={form}
        field="publicBonusComment"
        errorKey="public_bonus_comment"
        label="Публичный комментарий (бонусы)"
        placeholder="При изменении бонусного баланса"
      />
      <TextField
        form={form}
        field="loyaltyAmount"
        errorKey="amount"
        label="Онлайн оплаты"
        type="number"
        placeholder="Сумма для изменения"
      />
      <TextField
        form={form}
        field="loyaltyComment"
        errorKey="loyalty_balance_comment"
        label={<>Комментарий (онлайн оплаты) {values.loyaltyAmount && REQUIRED}</>}
        placeholder="При изменении баланса программы лояльности"
      />
    </>
  );
};

/** Fields shared by `UserCreatePage` and `UserEditPage`; edit-only parts are gated by `mode`. */
export const ProfileFields = ({ form, mode, permissions }: Props) => {
  const { values } = form;
  const isEdit = mode === 'edit';
  const isService = isServiceRole(values.roles);
  const isPhoneRequired = !(isEdit && values.statusId === BLOCKED_STATUS);
  const isEmailRequired = values.roles[0] !== 'customer';

  return (
    <>
      <TextField form={form} field="name" errorKey="name" label="Имя" />
      <TextField
        form={form}
        field="email"
        errorKey="email"
        label={<>Email {!isEdit && isEmailRequired && REQUIRED}</>}
      />
      <TextField
        form={form}
        field="phone"
        errorKey="phone"
        type="tel"
        label={<>Телефон {isPhoneRequired && REQUIRED}</>}
      />
      {isEdit && isService && (
        <>
          <CheckField
            form={form}
            field="emailNotifications"
            errorKey="email_notifications_enabled"
            label="Email-уведомления"
            hint="Отправка email уведомлений о назначении заявки, смене статуса заявки, входящем сообщении от клиента"
          />
          <TextField
            form={form}
            field="contactPhone"
            errorKey="contact_phone"
            type="tel"
            label={<>Контактный телефон {REQUIRED}</>}
            placeholder="79991234567 или 79991234567,79997654321"
            disabled={!permissions.canEditContactPhone}
          />
        </>
      )}
      {/* Create: country→region→city. Edit СТО: city+address block (legacy order, no country/region). */}
      {!isEdit && <PlaceFields form={form} />}
      {isEdit && isService && <ServiceLocationFields form={form} />}
      <RolesField form={form} />
      {hasAutoparkRole(values.roles) && <AutoparkFields form={form} />}
      <FormField label={<>Статус {REQUIRED}</>} errors={form.errors.status_id}>
        <FilterSelect
          options={USER_STATUS_OPTIONS}
          value={values.statusId}
          placeholder="Статус"
          width={300}
          isClearable={false}
          onChange={(statusId) =>
            form.patch(
              { statusId, payOnline: statusId === VERIFIED_STATUS ? values.payOnline : false },
              'status_id',
            )
          }
        />
      </FormField>
      <TextField
        form={form}
        field="password"
        errorKey="password"
        type="password"
        label={<>Пароль {!isEdit && REQUIRED}</>}
      />
      <TextField
        form={form}
        field="passwordRepeat"
        errorKey="password_repeat"
        type="password"
        label={<>Повторите пароль {!isEdit && REQUIRED}</>}
      />
      <CheckField form={form} field="generatePassword" errorKey="generate_password" label="Сгенерировать пароль" />
      <TextField form={form} field="balance" errorKey="balance" label="Баланс" />
      {isEdit && <BalanceCommentFields form={form} />}
      {(isEdit ? !isService : isCustomerRole(values.roles)) && <LoyaltyLevelFields form={form} />}
      {(isEdit || isService) && (
        <TextField form={form} field="marketingText" errorKey="marketing_text" label="Маркетинговый текст" maxLength={200} />
      )}
      {isService && <ServiceProfileFields form={form} mode={mode} permissions={permissions} />}
    </>
  );
};
